import React, { useState, useRef, useEffect } from 'react';

import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Box,
	Typography,
	Paper,
	Button,
	TextField,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Skeleton,
	CircularProgress,
	Tabs,
	Tab,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stack,
	MenuItem,
	Select,
	useTheme
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import SendIcon from '@mui/icons-material/Send';

import UserDocumentRequest from '../molecules/AIAgents/UserDocumentRequests';

import { useAppDispatch, useAppSelector } from '../../../redux/store';

import {
	createConversation,
	listPersonalSharedDocumentsRequests,
	readConversation,
	readSingleConversation,
	searchUsersDocumentRequests,
	storeFunctionCallResults
} from '../../../redux/slices/ai-assistant';

import {
	AgentType,
	Conversation,
	SharedDocumentsRequestFunctionNames,
	SharedDocumentsRequestType
} from '../../../types/ai-assistant';
import { RequestStatus } from '../../../redux/utils';
import { listRequests } from '../../../redux/slices/sharedDocuments';
import { LazyListRequest } from '../../../types/types';
import { formatDate } from '../../../utils';
import { useNavigate } from 'react-router-dom';
import { SharedDocumentsRequest } from '../../../types/sharedDocuments';
import { organisationRoles } from '../../../pages/Dashboard/utils';
import { Organisation } from '../../../types/organisation';

type ViewMode = 'table' | 'card';

const sanitizeName = (name: string) => {
	return name.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
};

// Helper function to safely include a parameter only if it has a valid value
const includeParam = (param: any): boolean => {
	return param !== undefined && param !== null && param !== '';
};

const functionNameTriggers = [
	'list_all_document_requests',
	'list_user_shared_documents_requests',
	'list_user_personal_shared_documents_requests'
];

// Add these skeleton components for loading states
const TableSkeleton = ({ isMobile }: { isMobile: boolean }) => (
	<TableContainer
		component={Paper}
		elevation={0}
		sx={{
			mb: 2,
			overflow: 'visible',
			maxHeight: 'none',
			borderRadius: 2,
			boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
			border: '1px solid rgba(0, 0, 0, 0.04)'
		}}
	>
		<Table aria-label="loading document requests table" size={isMobile ? 'small' : 'medium'}>
			<TableHead>
				<TableRow sx={{ bgcolor: 'action.hover' }}>
					<TableCell sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2 }}>Name</TableCell>
					<TableCell
						sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2, display: isMobile ? 'none' : 'table-cell' }}
					>
						Requested date
					</TableCell>
					<TableCell
						sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2, display: isMobile ? 'none' : 'table-cell' }}
					>
						Due date
					</TableCell>
					<TableCell sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2 }}>Progress</TableCell>
					<TableCell align="center" sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2 }}>
						Action
					</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{[...Array(5)].map((_, idx) => (
					<TableRow key={idx}>
						<TableCell sx={{ py: isMobile ? 1.2 : 1.8 }}>
							<Skeleton animation="wave" width="80%" />
						</TableCell>
						<TableCell sx={{ py: isMobile ? 1.2 : 1.8, display: isMobile ? 'none' : 'table-cell' }}>
							<Skeleton animation="wave" width="70%" />
						</TableCell>
						<TableCell sx={{ py: isMobile ? 1.2 : 1.8, display: isMobile ? 'none' : 'table-cell' }}>
							<Skeleton animation="wave" width="60%" />
						</TableCell>
						<TableCell sx={{ py: isMobile ? 1.2 : 1.8 }}>
							<Skeleton animation="wave" width="40%" />
						</TableCell>
						<TableCell align="center" sx={{ py: isMobile ? 1.2 : 1.8 }}>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width={isMobile ? 60 : 80}
								height={isMobile ? 24 : 30}
								sx={{ borderRadius: 1.5, mx: 'auto' }}
							/>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	</TableContainer>
);

const CardSkeleton = ({ isMobile }: { isMobile: boolean }) => (
	<Paper
		elevation={0}
		sx={{
			p: isMobile ? 2 : 2.5,
			mb: 2,
			borderRadius: 2,
			boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
			border: '1px solid rgba(0, 0, 0, 0.04)',
			width: isMobile ? '100%' : 'auto'
		}}
	>
		<Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1.5 : 2 }}>
			<Skeleton
				animation="wave"
				variant="circular"
				width={isMobile ? 30 : 40}
				height={isMobile ? 30 : 40}
				sx={{ mr: 2 }}
			/>
			<Box sx={{ width: '100%' }}>
				<Skeleton animation="wave" height={isMobile ? 20 : 24} width="40%" />
				<Skeleton animation="wave" height={isMobile ? 16 : 18} width="30%" />
			</Box>
		</Box>
		<Skeleton animation="wave" height={isMobile ? 14 : 16} />
		<Skeleton animation="wave" height={isMobile ? 14 : 16} />
		<Skeleton animation="wave" height={isMobile ? 14 : 16} width="80%" />
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				mt: isMobile ? 1.5 : 2,
				pt: 1.5,
				borderTop: '1px solid rgba(0,0,0,0.06)'
			}}
		>
			<Skeleton animation="wave" height={isMobile ? 18 : 20} width="30%" />
			<Skeleton
				animation="wave"
				variant="rectangular"
				width={isMobile ? 60 : 80}
				height={isMobile ? 24 : 30}
				sx={{ borderRadius: 1.5 }}
			/>
		</Box>
	</Paper>
);

type selectedOrganisationType = {
	messageId: string;
	selectedOrganisation: string;
};

const DocumentRequestsAssistant = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const theme = useTheme();
	const isDark = theme.palette.mode === 'dark';
	const [conversationId, setConversationId] = useState('');
	const [input, setInput] = useState('');
	const [messages, setMessages] = useState<(Conversation & { viewMode?: ViewMode })[]>([]);
	const [defaultViewMode, setDefaultViewMode] = useState<ViewMode>(() => {
		const savedView = localStorage.getItem('documentRequestsDefaultView');
		return (savedView as ViewMode) || 'table';
	});
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const [isMobile, setIsMobile] = useState(false);
	const [isLoadingInitialConversation, setIsLoadingInitialConversation] = useState(false);
	const [hasAttemptedConversationLoad, setHasAttemptedConversationLoad] = useState(false);
	const [organisationId, setOrganisationId] = useState('');
	const [alreadyOpened, setAlreadyOpened] = useState(false);
	const [isTabSwitching, setIsTabSwitching] = useState(false); // Flag for tab switching
	const [selectedOrganisation, setSelectedOrganisation] = useState<selectedOrganisationType[]>([]);

	const [
		user,
		recentConversations,
		recentConversationRequest,
		singleConversation,
		singleConversationRequest,
		organisation,
		_sharedDocumentsList
	] = useAppSelector((state) => [
		state.user.currentUser,
		state.aiAssistant.recentConversations.documentRequestAgent,
		state.aiAssistant.recentConversationRequest,
		state.aiAssistant.singleConversation,
		state.aiAssistant.readSingleConversationRequest,
		state.organisation.current,
		state.sharedDocuments.organisationRequestsList
	]);

	// Effect to fetch user's existing conversation with document request agent
	useEffect(() => {
		if (user?.id && open && !alreadyOpened) {
			setIsLoadingInitialConversation(true);
			setHasAttemptedConversationLoad(false);
			dispatch(readConversation({ userId: user.id, agentType: AgentType.DOCUMENT_REQUEST_AGENT }));
			setAlreadyOpened(true);
		}
	}, [user?.id, open, alreadyOpened]);

	useEffect(() => {
		return () => {
			setAlreadyOpened(false);
		};
	}, []);

	useEffect(() => {
		const role = user?.roles.find((r) => organisationRoles.includes(r.roleName));
		const organisationId = organisation?.id || role?.organisationId;
		organisationId && setOrganisationId(organisationId);
	}, [organisation, user?.roles]);

	// Effect to handle when conversation list is loaded
	useEffect(() => {
		if (recentConversationRequest.status === RequestStatus.SUCCESS && user?.id) {
			if (recentConversations?.items?.length > 0 && recentConversations.items[0]?.id) {
				// Existing conversation found - use it
				const existingConversationId = recentConversations.items[0].id;
				setConversationId(existingConversationId);
				dispatch(
					readSingleConversation({
						userId: user.id,
						conversationId: existingConversationId
					})
				);
				// Keep loading state true until single conversation is loaded
			} else {
				// No existing conversation - will create one on first message
				setIsLoadingInitialConversation(false);
				setHasAttemptedConversationLoad(true);
				setMessages([]);
			}
		} else if (recentConversationRequest.status === RequestStatus.ERROR) {
			setIsLoadingInitialConversation(false);
			setHasAttemptedConversationLoad(true);
		}
	}, [recentConversationRequest, user?.id]);

	// Effect to handle when single conversation is loaded
	useEffect(() => {
		if (singleConversationRequest.status === RequestStatus.SUCCESS && conversationId) {
			setMessages(singleConversation?.items || []);
			setIsLoadingInitialConversation(false);
			setHasAttemptedConversationLoad(true);
		} else if (singleConversationRequest.status === RequestStatus.ERROR) {
			setIsLoadingInitialConversation(false);
			setHasAttemptedConversationLoad(true);
		}
	}, [singleConversationRequest, conversationId]);

	useEffect(() => {
		const checkScreenSize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkScreenSize();

		window.addEventListener('resize', checkScreenSize);

		return () => {
			window.removeEventListener('resize', checkScreenSize);
		};
	}, []);

	// Update the useEffect for scrolling
	useEffect(() => {
		if (messagesEndRef.current && !isTabSwitching) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [messages.length]); // Only depend on message count, not content

	// Add a useEffect to reset tab switching flag
	useEffect(() => {
		if (isTabSwitching) {
			// Reset the flag after a short delay to prevent it from affecting future updates
			const timer = setTimeout(() => {
				setIsTabSwitching(false);
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [isTabSwitching]);

	// Effect to scroll to the bottom when the dialog is opened
	useEffect(() => {
		if (open && messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [open]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInput(e.target.value);
	};

	const handleDefaultViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newViewMode = event.target.value as ViewMode;
		setDefaultViewMode(newViewMode);
		localStorage.setItem('documentRequestsDefaultView', newViewMode);
	};

	const handleApplyAll = () => {
		setMessages((prevMessages) =>
			prevMessages.map((msg) => ({
				...msg,
				viewMode: defaultViewMode
			}))
		);
	};

	const handleSendMessage = () => {
		if (input.trim() === '') return;

		// Add the user message
		const newUserMessage: Conversation = {
			text: input,
			sender: 'user'
		};

		setMessages([...messages, { ...newUserMessage, viewMode: defaultViewMode }]);
		setInput('');

		// Show loading message while waiting for response
		const loadingMessage: Conversation = {
			text: '',
			sender: 'assistant',
			isLoading: true
		};

		setMessages((prev) => [...prev, { ...loadingMessage, viewMode: defaultViewMode }]);

		// Use existing conversationId if available, otherwise a new one will be created
		dispatch(
			createConversation({
				userId: user?.id || '',
				...(conversationId && { conversationId }),
				isOrganisation: organisationId ? true : false,
				query: input,
				agentType: AgentType.DOCUMENT_REQUEST_AGENT
			})
		).then((res) => {
			const data = res.payload as any;
			const response = data.response;

			// If this was the first message, save the new conversation ID
			if (!conversationId && data.conversationId) {
				setConversationId(data.conversationId);
			}

			// Create the assistant message based on the response type
			const assistantMessage: Conversation = {
				text: response.answerText,
				sender: 'assistant',
				id: data.kbMessageId || ''
			};

			// Replace loading message with actual response
			setMessages((prevMessages) =>
				prevMessages.slice(0, -1).concat({
					...assistantMessage,
					viewMode: defaultViewMode // Override any view mode with the default
				})
			);

			// Process the new message's function call if any
			try {
				const jsonData = JSON.parse(response.answerText);

				if (jsonData.function_call) {
					const functionNameMatch = jsonData.function_call.match(/^([^(]+)/);
					const userMatch = jsonData.function_call.match(/fullName=["']([^"']+)["']/);

					// Safely extract optional parameters with support for both quote types
					const dataTypeMatch = jsonData.function_call.match(/dataType=["']([^"']+)["']/);
					const nextMatch = jsonData.function_call.match(/next=["']([^"']+)["']/);
					// Update limit to handle both quoted string numbers and direct numeric values
					const limitMatch = jsonData.function_call.match(/limit=(["']([^"']+)["']|\d+)/);
					const startDateMatch = jsonData.function_call.match(/startDate=["']([^"']+)["']/);
					const endDateMatch = jsonData.function_call.match(/endDate=["']([^"']+)["']/);
					const overdueMatch = jsonData.function_call.match(
						/overdue=(["']([^"']+)["']|true|false|True|False|TRUE|FALSE)/
					);
					const statusMatch = jsonData.function_call.match(/status=["']([^"']+)["']/);
					const requestStatusMatch = jsonData.function_call.match(/requestStatus=["']([^"']+)["']/);

					// Get values only if matches exist
					const dataType = dataTypeMatch?.[1]?.trim();
					const next = nextMatch?.[1]?.trim();
					// Extract limit value from the appropriate capture group or direct match
					const limit = limitMatch ? (limitMatch[2] ? limitMatch[2].trim() : limitMatch[1].trim()) : null;
					const startDate = startDateMatch?.[1]?.trim();
					const endDate = endDateMatch?.[1]?.trim();
					// Extract overdue value from the appropriate capture group or direct match
					const overdue = overdueMatch
						? overdueMatch[2]
							? overdueMatch[2].trim()
							: overdueMatch[1].trim()
						: null;
					const status = statusMatch?.[1]?.trim();
					const requestStatus = requestStatusMatch?.[1]?.trim();

					console.log(
						jsonData.function_call,
						dataType,
						next,
						limit,
						startDate,
						endDate,
						overdue,
						status,
						requestStatus
					);

					if (functionNameMatch && functionNameMatch[1]) {
						const funcName = functionNameMatch[1].trim();

						if (functionNameTriggers.includes(funcName)) {
							const payload: LazyListRequest = {
								limit: 10,
								next: null
							};

							if (funcName === 'list_all_document_requests') {
								dispatch(
									listRequests({
										organisationId,
										...payload,
										...(includeParam(dataType) && { dataType }),
										...(includeParam(next) && { next }),
										...(includeParam(limit) && { limit: Number(limit) }),
										...(includeParam(startDate) && { startDate }),
										...(includeParam(endDate) && { endDate }),
										...(includeParam(overdue) && { overdue: overdue as string }),
										...(includeParam(status) && { status }),
										...(includeParam(requestStatus) && { requestStatus })
									})
								).then((res) => {
									const dataList = res.payload as any;
									dispatch(
										storeFunctionCallResults({
											messageId: data.kbMessageId || '',
											functionCallResults: JSON.stringify(dataList.items)
										})
									);
									{
										/* eslint-disable no-mixed-spaces-and-tabs */
									}
									// Update message with function call results and override view mode
									setMessages((prevMessages) =>
										prevMessages.map((msg) =>
											msg.id === data.kbMessageId
												? {
													...msg,
													functionCallResults: JSON.stringify(dataList.items),
													viewMode: defaultViewMode // Override with default view mode
												  }
												: msg
										)
									);
								});
							} else if (funcName === 'list_user_shared_documents_requests') {
								dispatch(
									searchUsersDocumentRequests({
										organisationId,
										fullName: sanitizeName(userMatch[1]),
										...payload,
										...(includeParam(dataType) && { dataType }),
										...(includeParam(next) && { next }),
										...(includeParam(limit) && { limit: Number(limit) }),
										...(includeParam(startDate) && { startDate }),
										...(includeParam(endDate) && { endDate }),
										...(includeParam(overdue) && { overdue: overdue as string }),
										...(includeParam(status) && { status }),
										...(includeParam(requestStatus) && { requestStatus })
									})
								).then((res) => {
									const dataSearch = res.payload as any;
									dispatch(
										storeFunctionCallResults({
											messageId: data.kbMessageId || '',
											functionCallResults: JSON.stringify(dataSearch)
										})
									);

									// Update message with function call results and override view mode
									setMessages((prevMessages) =>
										prevMessages.map((msg) =>
											msg.id === data.kbMessageId
												? {
													...msg,
													functionCallResults: JSON.stringify(dataSearch),
													viewMode: defaultViewMode // Override with default view mode
												  }
												: msg
										)
									);
								});
							} else if (funcName === 'list_user_personal_shared_documents_requests') {
								dispatch(
									listPersonalSharedDocumentsRequests({
										userId: user?.id || '',
										...payload,
										...(includeParam(dataType) && { dataType }),
										...(includeParam(next) && { next }),
										...(includeParam(limit) && { limit: Number(limit) }),
										...(includeParam(startDate) && { startDate }),
										...(includeParam(endDate) && { endDate }),
										...(includeParam(overdue) && { overdue: overdue as string }),
										...(includeParam(status) && { status }),
										...(includeParam(requestStatus) && { requestStatus })
									})
								).then((res) => {
									const dataSearch = res.payload as any;
									dispatch(
										storeFunctionCallResults({
											messageId: data.kbMessageId || '',
											functionCallResults: JSON.stringify(dataSearch)
										})
									);

									// Update message with function call results and override view mode
									setMessages((prevMessages) =>
										prevMessages.map((msg) =>
											msg.id === data.kbMessageId
												? {
													...msg,
													functionCallResults: JSON.stringify(dataSearch),
													viewMode: defaultViewMode // Override with default view mode
												  }
												: msg
										)
									);

									{
										/* eslint-enable no-mixed-spaces-and-tabs */
									}
								});
							}
						}
					}
				}
			} catch (error) {
				console.error('Error parsing message:', error);
				// Not a JSON or doesn't have function_call, ignore
			}
		});
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendMessage();
		}
	};

	// Add this helper function to get selected organization for a message
	const getSelectedOrganisation = (messageId: string | undefined): string => {
		if (!messageId) return '';
		return selectedOrganisation.find((org) => org.messageId === messageId)?.selectedOrganisation || '';
	};

	// Add this helper function to update selected organization
	const updateSelectedOrganisation = (messageId: string | undefined, orgName: string) => {
		if (!messageId) return;
		setSelectedOrganisation((prev) => {
			const existing = prev.find((org) => org.messageId === messageId);
			if (existing) {
				return prev.map((org) =>
					org.messageId === messageId ? { ...org, selectedOrganisation: orgName } : org
				);
			}
			return [...prev, { messageId, selectedOrganisation: orgName }];
		});
	};

	// Modify the renderMessage function to show skeletons while loading
	const renderMessage = (message: Conversation & { viewMode?: ViewMode }, index: number) => {
		if (message.sender === 'user') {
			return (
				<Paper
					key={index}
					elevation={0}
					sx={{
						p: 2.5,
						mb: 2,
						maxWidth: '80%',
						alignSelf: 'flex-end',
						bgcolor: '#1E40AF',
						color: 'white',
						borderRadius: '20px 20px 0 20px',
						boxShadow: '0 4px 20px rgba(30, 64, 175, 0.15)'
					}}
				>
					<Typography variant="body1" sx={{ lineHeight: 1.6 }}>
						{message.text}
					</Typography>
				</Paper>
			);
		}

		// Show loading spinner for assistant messages that are still loading
		if (message.isLoading) {
			return (
				<Paper
					key={index}
					elevation={0}
					sx={{
						p: 2.5,
						mb: 2,
						maxWidth: '80%',
						alignSelf: 'flex-start',
						bgcolor: theme.palette.background.paper,
						color: 'text.primary',
						borderRadius: '20px 20px 20px 0',
						boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '48px',
						minWidth: '120px'
					}}
				>
					<CircularProgress size={24} sx={{ color: '#1E40AF' }} />
				</Paper>
			);
		}

		// For assistant messages, try to parse the JSON response
		let parsedData = { answer: '', function_call: '', dataType: '', functionName: '', user_id: '' };
		let functionResults: any = null;
		let isLoading = false;

		try {
			// Attempt to parse the message text as JSON
			if (message.text && typeof message.text === 'string') {
				const jsonData = JSON.parse(message.text);
				parsedData = {
					answer: jsonData.answer || '',
					function_call: jsonData.function_call || '',
					dataType: '',
					functionName: '',
					user_id: ''
				};

				// Extract dataType from function_call if present
				if (parsedData.function_call) {
					const dataTypeMatch = parsedData.function_call.match(/dataType=["']([^"']+)["']/);
					if (dataTypeMatch && dataTypeMatch[1]) {
						parsedData.dataType = dataTypeMatch[1];
					}

					const userMatch = parsedData.function_call.match(/fullName=["']([^"']+)["']/);
					if (userMatch && userMatch[1]) {
						parsedData.user_id = userMatch[1];
					}

					// Extract function name from function_call
					const functionNameMatch = parsedData.function_call.match(/^([^(]+)/);
					if (functionNameMatch && functionNameMatch[1]) {
						parsedData.functionName = functionNameMatch[1].trim();
					}

					// Check if this message has a function call but no results yet
					if (functionNameTriggers.includes(parsedData.functionName) && !message.functionCallResults) {
						isLoading = true;
					}
				}
			}

			// Try to parse function call results if available
			if (message.functionCallResults) {
				functionResults = JSON.parse(message.functionCallResults);
				isLoading = false;
			}
		} catch (error) {
			console.error('Error parsing message:', error);
			// If parsing fails, just use the text as is
			parsedData.answer = message.text;
		}

		// Update the renderTabControls function
		const renderTabControls = () => {
			if (!message.id || !functionResults || !functionNameTriggers.includes(parsedData.functionName)) {
				return null;
			}

			const currentViewMode = message.viewMode || (hasUserDocumentRequests ? 'card' : 'table');
			const currentSelectedOrg = getSelectedOrganisation(message.id);

			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%'
					}}
				>
					<Tabs
						value={currentViewMode}
						onChange={(_, newValue: ViewMode) => {
							if (message.id) {
								setIsTabSwitching(true);
								setMessages((prevMessages) =>
									prevMessages.map((msg) =>
										msg.id === message.id ? { ...msg, viewMode: newValue } : msg
									)
								);
							}
						}}
						sx={{
							mb: 2,
							'& .MuiTabs-indicator': {
								backgroundColor: '#1E40AF'
							},
							'& .MuiTab-root': {
								textTransform: 'none',
								fontWeight: 500,
								fontSize: '0.875rem',
								minWidth: 100,
								px: 3
							}
						}}
					>
						<Tab
							value="table"
							label="Table"
							sx={{
								color: currentViewMode === 'table' ? '#1E40AF' : 'text.secondary',
								'&.Mui-selected': { color: '#1E40AF', fontWeight: 600 }
							}}
						/>
						<Tab
							value="card"
							label="Card"
							sx={{
								color: currentViewMode === 'card' ? '#1E40AF' : 'text.secondary',
								'&.Mui-selected': { color: '#1E40AF', fontWeight: 600 }
							}}
						/>
					</Tabs>
					<Box sx={{ mr: 2 }}>
						<Select
							value={currentSelectedOrg}
							onChange={(e) => {
								const messageId = message.id;
								if (messageId) {
									updateSelectedOrganisation(messageId, e.target.value);
								}
							}}
							displayEmpty
							sx={{
								minWidth: 200,
								'& .MuiSelect-select': {
									py: 1,
									backgroundColor: 'background.paper',
									borderRadius: 1,
									fontSize: '0.875rem',
									fontWeight: 500,
									color: 'text.primary',
									'&:focus': {
										backgroundColor: 'background.paper'
									}
								},
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'divider',
									'&:hover': {
										borderColor: 'rgba(30, 64, 175, 0.3)'
									}
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: '#1E40AF',
									borderWidth: '1px'
								},
								'&:hover .MuiOutlinedInput-notchedOutline': {
									borderColor: 'rgba(30, 64, 175, 0.3)'
								},
								'& .MuiSelect-icon': {
									color: 'rgba(0, 0, 0, 0.54)',
									right: 8
								}
							}}
							MenuProps={{
								PaperProps: {
									sx: {
										mt: 1,
										boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
										borderRadius: 2,
										'& .MuiMenuItem-root': {
											fontSize: '0.875rem',
											py: 1.5,
											px: 2,
											'&:hover': {
												backgroundColor: 'rgba(30, 64, 175, 0.08)'
											},
											'&.Mui-selected': {
												backgroundColor: 'rgba(30, 64, 175, 0.12)',
												'&:hover': {
													backgroundColor: 'rgba(30, 64, 175, 0.16)'
												}
											}
										}
									}
								}
							}}
						>
							<MenuItem
								value=""
								sx={{
									color: 'text.secondary',
									fontWeight: 500
								}}
							>
								All Organizations
							</MenuItem>
							{Array.from(
								new Set(
									functionResults
										.map((request: { organisation: Organisation }) => request.organisation?.name)
										.filter(Boolean)
								)
							).map((org: unknown, index) => (
								<MenuItem
									key={index}
									value={org as string}
									sx={{
										color: 'text.primary',
										'&.Mui-selected': {
											color: '#1E40AF',
											fontWeight: 500
										}
									}}
								>
									{org as string}
								</MenuItem>
							))}
						</Select>
					</Box>
				</Box>
			);
		};

		// For table and card views, we'll show either depending on the view mode
		const hasUserDocumentRequests =
			parsedData.functionName === SharedDocumentsRequestFunctionNames.LIST_USER_SHARED_DOCUMENTS_REQUESTS ||
			parsedData.dataType === SharedDocumentsRequestType.CARD_SHARED_DOCUMENTS_REQUEST;

		const hasAllDocumentRequests =
			parsedData.functionName === SharedDocumentsRequestFunctionNames.LIST_ALL_DOCUMENT_REQUESTS ||
			parsedData.dataType === SharedDocumentsRequestType.TABLE_SHARED_DOCUMENTS_REQUEST;

		/* eslint-disable no-mixed-spaces-and-tabs */
		if ((hasUserDocumentRequests || hasAllDocumentRequests) && functionResults) {
			const userData = message.data?.user || { name: 'User', status: 'Pending' };
			const currentSelectedOrg = getSelectedOrganisation(message.id);
			const documentRequests =
				currentSelectedOrg === ''
					? functionResults
					: functionResults.filter(
						(request: SharedDocumentsRequest) => request?.organisation?.name === currentSelectedOrg
					  );

			// Determine which view to show based on viewMode with proper defaults
			const viewMode = message.viewMode || (hasUserDocumentRequests ? 'card' : 'table');
			return (
				<Box key={index} sx={{ alignSelf: 'flex-start', width: '100%', mb: 2 }}>
					<Paper
						elevation={0}
						sx={{
							p: 2.5,
							mb: 2,
							maxWidth: '80%',
							bgcolor: theme.palette.background.paper,
							color: 'text.primary',
							borderRadius: '20px 20px 20px 0',
							boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)'
						}}
					>
						<Typography variant="body1" sx={{ lineHeight: 1.6 }}>
							{parsedData.answer}
						</Typography>
					</Paper>
					{/* eslint-enable no-mixed-spaces-and-tabs */}
					{isLoading ? (
						<>
							<Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, ml: 1 }}>
								Fetching document requests...
							</Typography>
							{viewMode === 'card' ? (
								<CardSkeleton isMobile={isMobile} />
							) : (
								<TableSkeleton isMobile={isMobile} />
							)}
						</>
					) : documentRequests.length === 0 ? (
						<Paper
							elevation={0}
							sx={{
								p: 3,
								mb: 2,
								borderRadius: 2,
								boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
								border: '1px solid rgba(0, 0, 0, 0.04)',
								textAlign: 'center'
							}}
						>
							<Typography variant="body1" sx={{ color: 'text.secondary' }}>
								No document requests found
							</Typography>
						</Paper>
					) : (
						<>
							{message.id && renderTabControls()}

							{viewMode === 'card' ? (
								<UserDocumentRequest
									status={userData.status}
									sharedDocumentRequests={documentRequests}
									_onOpen={() => console.log('Opening document request for', userData.name)}
								/>
							) : (
								<TableContainer
									component={Paper}
									elevation={0}
									sx={{
										mb: 2,
										overflow: 'visible',
										maxHeight: 'none',
										borderRadius: 2,
										boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
										border: '1px solid rgba(0, 0, 0, 0.04)'
									}}
								>
									<Table
										aria-label="pending document requests table"
										size={isMobile ? 'small' : 'medium'}
									>
										<TableHead>
											<TableRow sx={{ bgcolor: 'action.hover' }}>
												<TableCell sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2 }}>
													Name
												</TableCell>
												<TableCell
													sx={{
														fontWeight: 600,
														py: isMobile ? 1.5 : 2,
														display: isMobile ? 'none' : 'table-cell'
													}}
												>
													Requested date
												</TableCell>
												<TableCell
													sx={{
														fontWeight: 600,
														py: isMobile ? 1.5 : 2,
														display: isMobile ? 'none' : 'table-cell'
													}}
												>
													Due date
												</TableCell>
												<TableCell sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2 }}>
													Progress
												</TableCell>
												<TableCell
													align="center"
													sx={{ fontWeight: 600, py: isMobile ? 1.5 : 2 }}
												>
													Action
												</TableCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{documentRequests?.map((request: SharedDocumentsRequest, idx: number) => (
												<TableRow
													key={idx}
													sx={{
														'&:last-child td, &:last-child th': { border: 0 },
														'&:hover': { bgcolor: 'rgba(0, 0, 0, 0.01)' }
													}}
												>
													<TableCell sx={{ py: isMobile ? 1.2 : 1.8 }}>
														{request.name}
														{isMobile && (
															<Typography
																variant="caption"
																display="block"
																color="text.secondary"
																sx={{ mt: 0.5 }}
															>
																Created: {formatDate(request.creationDateTime)}
															</Typography>
														)}
													</TableCell>
													<TableCell
														sx={{
															py: isMobile ? 1.2 : 1.8,
															display: isMobile ? 'none' : 'table-cell'
														}}
													>
														{formatDate(request.creationDateTime)}
													</TableCell>
													<TableCell
														sx={{
															py: isMobile ? 1.2 : 1.8,
															display: isMobile ? 'none' : 'table-cell'
														}}
													>
														{formatDate(request.expirationDateTime)}
													</TableCell>
													<TableCell sx={{ py: isMobile ? 1.2 : 1.8 }}>
														{request.stepsData?.length ?? 0} / {request.steps.length}
													</TableCell>
													<TableCell align="center" sx={{ py: isMobile ? 1.2 : 1.8 }}>
														<Button
															variant="contained"
															size={isMobile ? 'small' : 'medium'}
															sx={{
																textTransform: 'none',
																bgcolor: '#1E40AF',
																fontWeight: 500,
																borderRadius: 1.5,
																px: isMobile ? 1.5 : 2.5,
																py: isMobile ? 0.5 : 0.75,
																boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)',
																fontSize: isMobile ? '0.75rem' : '0.875rem',
																minWidth: isMobile ? '60px' : '80px',
																'&:hover': {
																	bgcolor: '#1E3A8A',
																	boxShadow: '0 6px 15px rgba(30, 64, 175, 0.25)'
																}
															}}
															onClick={() => {
																navigate(
																	`/organisations/${organisationId}/shared-documents-requests/${request.id}`
																);
															}}
														>
															Open
														</Button>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</>
					)}
				</Box>
			);
		}

		// Default view for other types of messages
		return (
			<Paper
				key={index}
				elevation={0}
				sx={{
					p: 2.5,
					mb: 2,
					maxWidth: '80%',
					alignSelf: 'flex-start',
					bgcolor: theme.palette.background.paper,
					color: 'text.primary',
					borderRadius: '20px 20px 20px 0',
					boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)'
				}}
			>
				<Typography variant="body1" sx={{ lineHeight: 1.6 }}>
					{parsedData.answer || message.text}
				</Typography>
			</Paper>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			PaperProps={{
				sx: {
					borderRadius: isMobile ? 0 : 3,
					height: isMobile ? '100vh' : '90vh',
					m: 0,
					maxWidth: isMobile ? '100%' : '1200px',
					width: '100%',
					overflow: 'hidden',
					background: isDark
						? theme.palette.background.paper
						: 'linear-gradient(to bottom, #ffffff, #f7f9fc)',
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
				}
			}}
			fullScreen={isMobile}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					position: 'relative',
					p: 3,
					borderBottom: `1px solid ${theme.palette.divider}`,
					bgcolor: theme.palette.background.paper,
					boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)'
				}}
			>
				<Stack
					direction="row"
					spacing={2}
					alignItems="center"
					sx={{
						backgroundColor: 'rgba(30, 64, 175, 0.04)',
						p: 1.5,
						borderRadius: 2,
						border: '1px solid rgba(30, 64, 175, 0.1)'
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 2,
							'& .MuiFormControlLabel-root': {
								margin: 0
							}
						}}
					>
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontWeight: 500,
								whiteSpace: 'nowrap',
								display: 'flex',
								alignItems: 'center',
								gap: 1
							}}
						>
							<Box
								component="span"
								sx={{
									width: 4,
									height: 4,
									borderRadius: '50%',
									bgcolor: 'primary.main',
									display: 'inline-block'
								}}
							/>
							Default View
						</Typography>
						<RadioGroup
							row
							value={defaultViewMode}
							onChange={handleDefaultViewChange}
							sx={{
								'& .MuiRadio-root': {
									padding: 0.5,
									color: 'rgba(0, 0, 0, 0.2)',
									'&.Mui-checked': {
										color: 'primary.main'
									}
								},
								'& .MuiFormControlLabel-root': {
									marginRight: 1
								}
							}}
						>
							<FormControlLabel
								value="table"
								control={<Radio size="small" />}
								label={
									<Typography
										variant="body2"
										sx={{
											color: 'text.secondary',
											fontSize: '0.8125rem',
											fontWeight: defaultViewMode === 'table' ? 500 : 400
										}}
									>
										Table
									</Typography>
								}
							/>
							<FormControlLabel
								value="card"
								control={<Radio size="small" />}
								label={
									<Typography
										variant="body2"
										sx={{
											color: 'text.secondary',
											fontSize: '0.8125rem',
											fontWeight: defaultViewMode === 'card' ? 500 : 400
										}}
									>
										Card
									</Typography>
								}
							/>
						</RadioGroup>
					</Box>
					<Box
						sx={{
							width: '1px',
							height: 24,
							bgcolor: 'rgba(0, 0, 0, 0.1)',
							mx: 1
						}}
					/>
					<Button
						variant="text"
						size="small"
						onClick={handleApplyAll}
						sx={{
							textTransform: 'none',
							color: 'primary.main',
							fontSize: '0.8125rem',
							fontWeight: 500,
							p: 0.5,
							minWidth: 'auto',
							'&:hover': {
								backgroundColor: 'rgba(30, 64, 175, 0.08)'
							}
						}}
					>
						Apply to All
					</Button>
				</Stack>

				<Typography
					variant="h5"
					component="h2"
					sx={{
						fontWeight: 700,
						letterSpacing: '0.05em',
						color: '#1E40AF',
						textAlign: 'center',
						position: 'absolute',
						left: '50%',
						transform: 'translateX(-50%)',
						width: 'fit-content',
						display: { xs: 'none', sm: 'block' }
					}}
				>
					Document Requests
				</Typography>

				<IconButton
					edge="end"
					color="inherit"
					onClick={onClose}
					aria-label="close"
					sx={{
						color: 'text.secondary',
						'&:hover': {
							background: 'rgba(0, 0, 0, 0.04)'
						}
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
				{/* Chat window */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						bgcolor: isDark ? theme.palette.background.default : '#F8FAFC'
					}}
				>
					{/* Messages area */}
					<Box
						sx={{
							flex: 1,
							overflowY: 'auto',
							p: 3,
							display: 'flex',
							flexDirection: 'column'
						}}
					>
						{isLoadingInitialConversation ? (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									height: '100%',
									flexDirection: 'column',
									gap: 2
								}}
							>
								<CircularProgress size={40} sx={{ color: '#1E40AF' }} />
								<Typography variant="body2" color="text.secondary">
									Loading conversation...
								</Typography>
							</Box>
						) : hasAttemptedConversationLoad && messages.length === 0 ? (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									height: '100%',
									color: 'text.secondary',
									textAlign: 'center',
									p: 3
								}}
							>
								<Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
									Document Requests Assistant
								</Typography>
								<Typography variant="body2" sx={{ maxWidth: 400 }}>
									Ask me about document requests from your users or organization. I can help you view,
									manage, and track document statuses.
								</Typography>
							</Box>
						) : (
							messages.map((message, index) => renderMessage(message, index))
						)}
						<div ref={messagesEndRef} />
					</Box>

					{/* Input area */}
					<Box
						sx={{
							p: 3,
							borderTop: `1px solid ${theme.palette.divider}`,
							bgcolor: theme.palette.background.paper,
							boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.03)',
							mt: 'auto'
						}}
					>
						<TextField
							fullWidth
							variant="outlined"
							placeholder="Type a message..."
							value={input}
							onChange={handleInputChange}
							onKeyPress={handleKeyPress}
							disabled={isLoadingInitialConversation}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											color="primary"
											onClick={handleSendMessage}
											disabled={input.trim() === '' || isLoadingInitialConversation}
											sx={{
												bgcolor: input.trim() ? 'rgba(30, 64, 175, 0.08)' : 'transparent',
												'&:hover': {
													bgcolor: 'rgba(30, 64, 175, 0.12)'
												}
											}}
										>
											<SendIcon />
										</IconButton>
									</InputAdornment>
								),
								sx: {
									borderRadius: 3,
									bgcolor: isDark ? theme.palette.action.hover : '#F9FAFB',
									py: 0.5,
									'&.Mui-focused': {
										boxShadow: '0 0 0 3px rgba(30, 64, 175, 0.12)'
									},
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: theme.palette.divider
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(30, 64, 175, 0.3)'
									},
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: '#1E40AF'
									}
								}
							}}
						/>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default DocumentRequestsAssistant;
